import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import "../../day-plan-styles/ExpandedTaskCard.css";
import { useTranslation } from "@preppa/translation";
import Button from "@ingka/button";
import dataIcon from "@ingka/ssr-icon/paths/data-chart";
import instructionsIcon from "@ingka/ssr-icon/paths/link-out";
import {
  PlanData,
  CustomTaskCard as CustomTaskCardType,
  TASK_STATUS,
} from "../../../../types/types";
import { updatePlanData } from "../../../../api/apiCallsPlanData";
import {
  getTaskDisplayName,
  getDisplayConversion,
  formatVolume,
  sanitizeDecimalInput,
  hasProducedInput,
  hasAvailableInput,
  isConvertibleUom,
  getComplementaryAutoUnit,
  normalizeUomCode,
} from "../../../../utils/planDataHelpers";
import { useDataContext } from "../../../../data-context/DataContextProvider";

interface CustomTaskCardProps {
  planData: PlanData;
  onToggle: () => void;
  currentStatus: string;
  customCard: CustomTaskCardType;
  onValuesChange?: (available: number, produced: number) => void;
  onViewBreakdown?: (planData: PlanData) => void;
}

function CustomTaskCard({
  planData,
  onToggle,
  currentStatus,
  customCard,
  onValuesChange,
  onViewBreakdown,
}: CustomTaskCardProps) {
  const { buCode, buType, dispatch } = useDataContext();
  const { translate } = useTranslation();

  const uomCode =
    planData.itemSalesPlanDtos[0]?.uomCode === "each"
      ? "PC"
      : planData.itemSalesPlanDtos[0]?.uomCode || "";

  const displayConversion = getDisplayConversion(planData);
  const conversionFactor = displayConversion?.conversionFactor ?? 1;
  const displayUom = displayConversion?.displayUom ?? uomCode;
  const taskDisplayName = getTaskDisplayName(planData);

  const slotTotalPlannedVol = customCard.totalPlannedVol;
  const slotTotalForecastedVol = customCard.totalForecastedVol;
  const currentTotalPlan = slotTotalPlannedVol || slotTotalForecastedVol;
  const displayTotalPlan = currentTotalPlan / conversionFactor;
  const originalTotal = slotTotalPlannedVol || slotTotalForecastedVol;

  const slotDto = customCard.itemSalesPlan;

  const showProduced = hasProducedInput(planData);
  const showAvailable = hasAvailableInput(planData);

  const slotProducedVol = showProduced ? slotDto.producedVol || 0 : 0;
  const slotAvailableVol = showAvailable ? slotDto.availableVol || 0 : 0;

  const [producedValue, setProducedValue] = useState<string>(
    formatVolume(slotProducedVol / conversionFactor),
  );
  const [availableValue, setAvailableValue] = useState<string>(
    formatVolume(slotAvailableVol / conversionFactor),
  );

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isEditingRef = useRef(false);
  const [flashField, setFlashField] = useState<string | null>(null);
  const flashTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isEditingRef.current) {
      setProducedValue(formatVolume(slotProducedVol / conversionFactor));
      setAvailableValue(formatVolume(slotAvailableVol / conversionFactor));
    }
  }, [slotProducedVol, slotAvailableVol, conversionFactor]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current);
      }
    };
  }, []);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    isEditingRef.current = true;
    e.target.select();
  };

  const handleBlur = () => {
    isEditingRef.current = false;
    setProducedValue((prev) => formatVolume(parseFloat(prev) || 0));
    setAvailableValue((prev) => formatVolume(parseFloat(prev) || 0));
  };

  const triggerFlash = (field: string) => {
    setFlashField(field);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashField(null), 400);
  };

  const exceedsServerLimit = (value: string): boolean => {
    const num = parseFloat(value) || 0;
    return Math.floor(Math.abs(num * conversionFactor)) > 99999999;
  };

  const handleNumericKeyDown =
    (field: string) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.ctrlKey || e.metaKey) return;
      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
        "Enter",
      ];
      if (allowedKeys.includes(e.key)) return;
      const input = e.currentTarget;
      const currentValue =
        field === "available" ? availableValue : producedValue;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const newValue =
        currentValue.substring(0, start) + e.key + currentValue.substring(end);
      const sanitized = sanitizeDecimalInput(newValue);
      if (
        sanitized.length !== newValue.length ||
        exceedsServerLimit(sanitized)
      ) {
        e.preventDefault();
        triggerFlash(field);
      }
    };

  const debouncedUpdate = useCallback(
    (
      newProducedVol: number,
      newAvailableVol: number,
      prevProducedVol: number,
      prevAvailableVol: number,
    ) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(async () => {
        const statusCode = slotDto.statusCode || "10";
        const comment = slotDto.comment || "";

        const success = await updatePlanData({
          itemCode: planData.itemCode,
          fromHour: customCard.fromHour,
          toHour: customCard.toHour,
          buCode,
          buType,
          statusCode,
          producedVol: newProducedVol * conversionFactor,
          availableVol: newAvailableVol * conversionFactor,
          comment,
          callBack: dispatch,
        });

        if (!success) {
          setProducedValue(String(prevProducedVol));
          setAvailableValue(String(prevAvailableVol));
          onValuesChange?.(
            prevAvailableVol * conversionFactor,
            prevProducedVol * conversionFactor,
          );
        }
      }, 500);
    },
    [
      customCard,
      slotDto,
      planData,
      buCode,
      buType,
      dispatch,
      conversionFactor,
      onValuesChange,
    ],
  );

  const processAvailableValue = (value: string) => {
    const prevProduced = parseFloat(producedValue) || 0;
    const prevAvailable = parseFloat(availableValue) || 0;
    setAvailableValue(value);

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      onValuesChange?.(
        numValue * conversionFactor,
        prevProduced * conversionFactor,
      );
      debouncedUpdate(prevProduced, numValue, prevProduced, prevAvailable);
    } else if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  };

  const handleAvailableChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const sanitized = sanitizeDecimalInput(rawValue);
    if (sanitized.length !== rawValue.length) triggerFlash("available");
    if (exceedsServerLimit(sanitized)) {
      triggerFlash("available");
      return;
    }
    processAvailableValue(sanitized);
  };

  const handleAvailablePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text");
    const input = e.currentTarget;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const rawValue =
      availableValue.substring(0, start) + text + availableValue.substring(end);
    const composed = sanitizeDecimalInput(rawValue);
    if (composed.length !== rawValue.length) triggerFlash("available");
    if (exceedsServerLimit(composed)) {
      triggerFlash("available");
      return;
    }
    processAvailableValue(composed);
  };

  const processProducedValue = (value: string) => {
    const prevProduced = parseFloat(producedValue) || 0;
    const prevAvailable = parseFloat(availableValue) || 0;
    setProducedValue(value);

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      onValuesChange?.(
        prevAvailable * conversionFactor,
        numValue * conversionFactor,
      );
      debouncedUpdate(numValue, prevAvailable, prevProduced, prevAvailable);
    } else if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  };

  const handleProducedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const sanitized = sanitizeDecimalInput(rawValue);
    if (sanitized.length !== rawValue.length) triggerFlash("produced");
    if (exceedsServerLimit(sanitized)) {
      triggerFlash("produced");
      return;
    }
    processProducedValue(sanitized);
  };

  const handleProducedPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text");
    const input = e.currentTarget;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const rawValue =
      producedValue.substring(0, start) + text + producedValue.substring(end);
    const composed = sanitizeDecimalInput(rawValue);
    if (composed.length !== rawValue.length) triggerFlash("produced");
    if (exceedsServerLimit(composed)) {
      triggerFlash("produced");
      return;
    }
    processProducedValue(composed);
  };

  const currentAvailable = parseFloat(availableValue) || 0;
  const currentProduced = parseFloat(producedValue) || 0;
  const remainingValue = (
    displayTotalPlan -
    currentAvailable -
    currentProduced
  ).toFixed(1);

  const rawUomCode = planData.itemSalesPlanDtos[0]?.uomCode;
  const hasActiveContainerConversions =
    customCard.parentPlanData?.containerConversionDtos?.some((c) => c.active) ??
    false;
  const showUomTable =
    isConvertibleUom(rawUomCode) || hasActiveContainerConversions;
  const displayBomUom = normalizeUomCode(rawUomCode) || uomCode;
  const complementaryAutoUnit = useMemo(
    () => getComplementaryAutoUnit(rawUomCode),
    [rawUomCode],
  );

  return (
    <div className="expanded-card-wrapper" onClick={onToggle}>
      <div className="production-information-container">
        <strong className="task-card-heading">
          {translate("dayPlan.card.taskProduction")}
        </strong>
        <table className="production-info-table">
          <tbody>
            <tr>
              <td className="task-card-label">
                {translate("dayPlan.card.planned")}
              </td>
              <td>
                <label> {formatVolume(displayTotalPlan)} </label>
              </td>
            </tr>
            {showAvailable && (
              <tr>
                <td className="task-card-label">
                  {translate("dayPlan.card.available")}
                </td>
                <td>
                  -
                  {currentStatus === TASK_STATUS.TODO ? (
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9.,]*"
                      value={availableValue}
                      onChange={handleAvailableChange}
                      onKeyDown={handleNumericKeyDown("available")}
                      onPaste={handleAvailablePaste}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      onClick={(e) => e.stopPropagation()}
                      disabled={currentStatus !== TASK_STATUS.TODO}
                      className={`${currentStatus !== TASK_STATUS.TODO ? "input-disabled" : ""}${flashField === "available" ? " input-invalid-flash" : ""}`}
                    />
                  ) : (
                    <label> {currentAvailable.toFixed(1)} </label>
                  )}
                </td>
              </tr>
            )}
            {showProduced && (
              <tr>
                <td className="task-card-label">
                  {translate("dayPlan.card.produced")}
                </td>
                <td>
                  -
                  {currentStatus === TASK_STATUS.IN_PROGRESS ? (
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9.,]*"
                      value={producedValue}
                      onChange={handleProducedChange}
                      onKeyDown={handleNumericKeyDown("produced")}
                      onPaste={handleProducedPaste}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      onClick={(e) => e.stopPropagation()}
                      disabled={currentStatus !== TASK_STATUS.IN_PROGRESS}
                      className={`${currentStatus !== TASK_STATUS.IN_PROGRESS ? "input-disabled" : ""}${flashField === "produced" ? " input-invalid-flash" : ""}`}
                    />
                  ) : (
                    <label> {currentProduced.toFixed(1)} </label>
                  )}
                </td>
              </tr>
            )}
            <tr>
              <td className="task-card-label">
                {translate("dayPlan.card.remaining")}
              </td>
              <td className="remaining-cell">
                <label>= {remainingValue} </label>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="item-information-container">
        <div className="item-information-header">
          <strong className="task-card-heading">
            {translate("dayPlan.card.itemInformation")}
          </strong>
        </div>
        <table className="item-info-table">
          <tbody>
            {/*             {taskDisplayName === "PLATE" && (
              <tr>
                <td>Commercial stock</td>
                <td>
                  {!isNaN(parseFloat(planData.commercialStock))
                    ? parseFloat(planData.commercialStock).toFixed(0)
                    : "N/A"}
                </td>
              </tr>
            )} */}
            <tr>
              <td>{translate("dayPlan.card.comment")}</td>
              <td>{planData.itemComment || ""}</td>
            </tr>
            <tr>
              <td>{translate("dayPlan.card.taskType")}</td>
              <td>
                <strong>{taskDisplayName}</strong>
              </td>
            </tr>
          </tbody>
        </table>

        {showUomTable && (
          <>
            <strong className="task-card-heading">
              {translate("dayPlan.card.unitsOfMeasurement")}
            </strong>
            <div className="uom-conversion-wrapper">
              <table className="uom-conversions-table">
                <tbody>
                  <tr className="uom-conversions-table-row">
                    <td>{displayBomUom}</td>
                    <td>{originalTotal.toFixed(1)}</td>
                  </tr>
                  {complementaryAutoUnit && (
                    <tr className="uom-conversions-table-row">
                      <td>{complementaryAutoUnit.label}</td>
                      <td>
                        {(
                          originalTotal / complementaryAutoUnit.conversionFactor
                        ).toFixed(1)}
                      </td>
                    </tr>
                  )}
                  {customCard.parentPlanData?.containerConversionDtos
                    ?.filter((c) => c.active)
                    .map((conversion) => (
                      <tr
                        key={conversion.containerId}
                        className="uom-conversions-table-row"
                      >
                        <td>{conversion.containerName}</td>
                        <td>
                          {(
                            originalTotal / conversion.containerConversion
                          ).toFixed(1)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="production-breakdown-button-wrapper">
          <Button
            ssrIcon={dataIcon}
            text={translate("dayPlan.breakdown.estimationBreakdown")}
            size="xsmall"
            type="secondary"
            className="production-breakdown-button"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onViewBreakdown?.(planData);
            }}
          />
          <Button
            text={translate("dayPlan.card.ppsiInstructions")}
            size="xsmall"
            ssrIcon={instructionsIcon}
            iconPosition="trailing"
            disabled={!planData.ppsiUrl}
            onClick={(e: any) => {
              e.stopPropagation();
              if (planData.ppsiUrl) {
                let url = planData.ppsiUrl;
                try {
                  url = atob(planData.ppsiUrl);
                } catch {
                  /* use raw */
                }
                window.open(url, "_blank", "noopener,noreferrer");
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default CustomTaskCard;
